// Dialog Super Admin: "Ver dados do boleto do banco".
// Consulta em tempo real o boleto direto no banco emissor (via gateway →
// payment) e exibe campos curados + accordion com a resposta bruta (JSON).
// Só busca com o dialog aberto (enabled: open). Espelha o padrão de
// src/features/notification-center/components/template-test-send-dialog.tsx.
import { ChevronDown, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import { Button } from "@/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useBankSlipBankData } from "@/services/orders/bank-slip-bank-data"

const prefix = "orders.transactionsTable.bankData"

type BankSlipBankDataDialogProps = {
  paymentTransactionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const BankSlipBankDataDialog = ({
  paymentTransactionId,
  open,
  onOpenChange,
}: BankSlipBankDataDialogProps) => {
  const { t } = useTranslation()

  // enabled: open → só consulta o banco quando o dialog abre.
  const { data, isLoading, isError } = useBankSlipBankData(
    paymentTransactionId,
    open
  )

  // Booleano do Itaú (nullable) → rótulo Sim/Não.
  const successLabel =
    data?.success === null || data?.success === undefined
      ? undefined
      : data.success
        ? t(`${prefix}.yes`)
        : t(`${prefix}.no`)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`${prefix}.title`)}</DialogTitle>
          <DialogDescription>
            {t(`${prefix}.description`)}
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div
            className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"
            data-testid="bank-slip-bank-data-loading"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
            {t(`${prefix}.loading`)}
          </div>
        )}

        {isError && !isLoading && (
          <p
            className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="bank-slip-bank-data-error"
          >
            {t(`${prefix}.loadError`)}
          </p>
        )}

        {data && !isLoading && !isError && (
          <div
            className="space-y-4"
            data-testid="bank-slip-bank-data-content"
          >
            <SectionCard
              title={t(`${prefix}.sectionBankSlip`)}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoItem
                  label={t(`${prefix}.gateway`)}
                  value={data.gateway}
                />
                <InfoItem
                  label={t(`${prefix}.externalStatus`)}
                  value={data.externalStatus}
                />
                <InfoItem
                  label={t(`${prefix}.statusComplement`)}
                  value={data.statusComplement}
                />
                <InfoItem
                  label={t(`${prefix}.ourNumber`)}
                  value={data.ourNumber}
                  copyable
                />
                <InfoItem
                  label={t(`${prefix}.amount`)}
                  value={data.amount}
                />
                <InfoItem
                  label={t(`${prefix}.dueDate`)}
                  value={data.dueDate}
                />
                <InfoItem
                  label={t(`${prefix}.issueDate`)}
                  value={data.issueDate}
                />
                <InfoItem
                  label={t(
                    `${prefix}.gatewayConfigurationId`
                  )}
                  value={
                    data.gatewayConfigurationId === null
                      ? null
                      : String(data.gatewayConfigurationId)
                  }
                />
                <InfoItem
                  label={t(`${prefix}.digitableLine`)}
                  value={data.digitableLine}
                  copyable
                  className="sm:col-span-2"
                />
                <InfoItem
                  label={t(`${prefix}.barCode`)}
                  value={data.barCode}
                  copyable
                  className="sm:col-span-2"
                />
                {successLabel !== undefined && (
                  <InfoItem
                    label={t(`${prefix}.success`)}
                    value={successLabel}
                  />
                )}
                {data.errorMessage && (
                  <InfoItem
                    label={t(`${prefix}.errorMessage`)}
                    value={data.errorMessage}
                    className="sm:col-span-2"
                  />
                )}
              </div>
            </SectionCard>

            <SectionCard
              title={t(`${prefix}.sectionPayer`)}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <InfoItem
                  label={t(`${prefix}.payerName`)}
                  value={data.payerName}
                />
                <InfoItem
                  label={t(`${prefix}.payerDocument`)}
                  value={data.payerDocument}
                  copyable
                />
              </div>
            </SectionCard>

            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="group w-full justify-between"
                >
                  {t(`${prefix}.rawResponse`)}
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <pre
                  className="mt-2 max-h-72 overflow-auto rounded-md border bg-muted p-3 text-xs"
                  data-testid="bank-slip-bank-data-raw"
                >
                  {JSON.stringify(
                    data.rawResponse,
                    null,
                    2
                  )}
                </pre>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t(`${prefix}.close`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
